import { useEffect, useRef } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumbs, OptionGroup, Page, PageHeader, Stepper } from './primitives';
import type { Crumb } from './primitives';
import { Icon } from './icons';

/**
 * OnboardingLayout (CO-90, audit W1, W2, B3): the one frame of the three
 * onboarding flows — a Magento project, a WordPress project and a server.
 * Shared visual grammar, not identical workflows: each flow keeps its own
 * steps (or none), but all of them have
 *
 * - breadcrumbs, then the title and one line of introduction for the
 *   current step;
 * - a vertical `Stepper` on the left on desktop when there is more than one
 *   step, collapsing under ~900 px to one "Step 2 of 6 · Server" line;
 * - the large control size (`.controls-lg`, 38 px);
 * - one footer: Cancel and Back on the left as secondary buttons, the
 *   primary action on the right;
 * - one validation pattern: validate on Continue, a message under each
 *   invalid field, focus moved to the first one (`invalidAttempt`).
 *
 * Width: a flow without steps is a narrow page (760 px). A flow with a rail
 * keeps the same 760 px content column and adds the rail beside it, so the
 * page is 760 + rail + gap wide; squeezing the rail into 760 px would leave
 * the Magento setup step about 500 px for its fields.
 */

export interface OnboardingStep {
  key: string;
  label: string;
}

type CancelAction = { to: string; label?: string; onClick?: never } | { onClick: () => void; label?: string; to?: never };

export function OnboardingLayout({
  breadcrumbs,
  title,
  intro,
  steps,
  current = 0,
  onSelectStep,
  stepsLabel = 'Steps',
  cancel,
  onBack,
  secondary,
  primary,
  footerNote,
  onSubmit,
  invalidAttempt = 0,
  outside,
  testId,
  children,
}: {
  breadcrumbs: Crumb[];
  /** The current step's title (the page's h1). */
  title: ReactNode;
  /** One line under the title. */
  intro?: ReactNode;
  /** More than one step shows the rail; one step or none shows no stepper. */
  steps?: OnboardingStep[];
  /** Index of the current step; `steps.length` once every step is done. */
  current?: number;
  /** Makes done steps buttons that go back to them. */
  onSelectStep?: (index: number) => void;
  stepsLabel?: string;
  cancel?: CancelAction;
  onBack?: () => void;
  /** Further secondary actions, after Cancel and Back. */
  secondary?: ReactNode;
  /** The primary action, right-aligned (a submit button when `onSubmit`). */
  primary?: ReactNode;
  /** One line above the footer buttons: why the primary is held, what happens next. */
  footerNote?: ReactNode;
  /** Makes the step body and footer one form. */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /** Incremented on every failed validation: focus moves to the first invalid field. */
  invalidAttempt?: number;
  /** Rendered after the page, outside its form: dialogs with forms of their own. */
  outside?: ReactNode;
  testId?: string;
  children: ReactNode;
}) {
  const rail = Boolean(steps && steps.length > 1);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (invalidAttempt > 0 && rootRef.current) focusFirstInvalid(rootRef.current);
  }, [invalidAttempt]);

  const body = (
    <>
      <div className="onboarding__body">{children}</div>
      <OnboardingFooter cancel={cancel} onBack={onBack} secondary={secondary} primary={primary} note={footerNote} />
    </>
  );

  return (
    <Page width={rail ? 'default' : 'narrow'} className={`onboarding controls-lg${rail ? ' onboarding--rail' : ''}`} testId={testId}>
      <div ref={rootRef} className="onboarding__root">
        <Breadcrumbs className="onboarding__crumbs" items={breadcrumbs} />
        <div className="onboarding__layout">
          {rail && steps ? (
            <div className="onboarding__rail">
              <Stepper label={stepsLabel} orientation="vertical" current={current} steps={steps} onSelect={onSelectStep} />
            </div>
          ) : null}
          <div className="onboarding__main">
            <div className="onboarding__heading">
              {rail && steps ? <p className="onboarding__progress">{progressLine(steps, current)}</p> : null}
              <PageHeader title={title} description={intro} />
            </div>
            {onSubmit ? (
              <form className="onboarding__form" onSubmit={onSubmit} noValidate>
                {body}
              </form>
            ) : (
              <div className="onboarding__form">{body}</div>
            )}
          </div>
        </div>
      </div>
      {outside}
    </Page>
  );
}

/** "Step 2 of 6 · Server": the rail's position in one line, for narrow screens. */
function progressLine(steps: OnboardingStep[], current: number) {
  if (current >= steps.length) return `All ${steps.length} steps done`;
  return `Step ${current + 1} of ${steps.length} · ${steps[current]?.label ?? ''}`;
}

function OnboardingFooter({
  cancel,
  onBack,
  secondary,
  primary,
  note,
}: {
  cancel?: CancelAction;
  onBack?: () => void;
  secondary?: ReactNode;
  primary?: ReactNode;
  note?: ReactNode;
}) {
  return (
    <div className="onboarding__footer">
      {note ? <p className="onboarding__footer-note">{note}</p> : null}
      <div className="onboarding__footer-row">
        <div className="onboarding__footer-start">
          {cancel ? (
            cancel.to !== undefined ? (
              <Link to={cancel.to} className="button button--secondary">
                {cancel.label ?? 'Cancel'}
              </Link>
            ) : (
              <button type="button" className="button button--secondary" onClick={cancel.onClick}>
                {cancel.label ?? 'Cancel'}
              </button>
            )
          ) : null}
          {onBack ? (
            <button type="button" className="button button--secondary" onClick={onBack}>
              <Icon name="back" size={16} />
              Back
            </button>
          ) : null}
          {secondary}
        </div>
        {primary ? <div className="onboarding__footer-end">{primary}</div> : null}
      </div>
    </div>
  );
}

const focusable = 'input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])';

/** Focuses the first field marked invalid, in document order. A marked
 * group (`data-onboarding-invalid`) gets its first focusable control. */
export function focusFirstInvalid(root: HTMLElement) {
  const first = root.querySelector<HTMLElement>('[aria-invalid="true"], [data-onboarding-invalid="true"]');
  if (!first) return;
  const target = first.matches(focusable) ? first : first.querySelector<HTMLElement>(focusable);
  if (target) {
    target.focus();
  } else {
    first.setAttribute('tabindex', '-1');
    first.focus();
  }
}

/** A single choice among a few option cards, with its question shown above them. */
export function OnboardingChoice({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  return (
    <div className="onboarding-choice" data-testid={testId}>
      <span className="onboarding-label" aria-hidden="true">
        {label}
      </span>
      <OptionGroup label={label}>{children}</OptionGroup>
    </div>
  );
}
